import React, { useCallback, useEffect, useState } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import useToggle from '@lyearn/core/hooks/useToggle';
import { emitter, EventsEnum } from '@lyearn/events';
import { useTranslation } from '@lyearn/i18n';
import { SidebarRightOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box, IconButton, Stack, Tooltip, useSnackbar } from '@lyearn/ui';
import { NoteTypeEnum } from '@/types/schema';

import { useCreateNoteMutation } from '../../graphql/mutations/generated/createNote';
import { NoteFragment } from '../../graphql/queries/generated/notes';
import { flexN, height100vhMinusHeader } from '../../styles';
import { KnowledgeDocsTabs, NotesViewMode } from '../../types';
import { MobileTabs } from '../ChatHeader';
import { CreateNewHoverPopover } from '../CreateNewHoverPopover';
import { KnowledgeBaseHeader } from '../KnowledgeBaseHeader';
import { Notes } from '../Notes';
import { NotesView } from '../NotesView';

interface NotesSectionProps {
  currentNote: NoteFragment | null;
  setCurrentNote: React.Dispatch<React.SetStateAction<NoteFragment | null>>;
  currentTab: MobileTabs;
  setHideNoteButtons: React.Dispatch<React.SetStateAction<boolean>>;
  hideNoteButtons: boolean;
}

function NotesSection({
  currentNote,
  setCurrentNote,
  currentTab,
  setHideNoteButtons,
  hideNoteButtons,
}: NotesSectionProps) {
  const [_, createNote] = useCreateNoteMutation();
  const { isMobile } = useScreen();
  const { showSnackbar, showErrorSnackbar } = useSnackbar();
  const { t } = useTranslation(['Common']);
  const [isCollapsed, { toggle: onToggle }] = useToggle(isMobile ? true : false);
  const [viewMode, setViewMode] = useState<NotesViewMode>(NotesViewMode.Notes);
  const [currentNotesTab, setCurrentNotesTab] = useState<KnowledgeDocsTabs>(
    KnowledgeDocsTabs.MyDocs,
  );

  useEffect(() => {
    // automatically switch to knowledge assets when a knowledge doc is created
    const callback = () => {
      if (viewMode !== NotesViewMode.KnowledgeAssets) {
        setViewMode(NotesViewMode.KnowledgeAssets);
      }
    };
    emitter.on(EventsEnum.KnowledgeDocCreation, callback);
    return () => emitter.off(EventsEnum.KnowledgeDocCreation, callback);
  }, []);

  const onNewNote = useCallback(() => {
    createNote({
      input: {
        title: t('Common:KnowledgeBase.UntitledNote'),
        type:
          viewMode === NotesViewMode.Notes ? NoteTypeEnum.KnowledgeBase : NoteTypeEnum.KnowledgeDoc,
      },
    })
      .then((res) => {
        if (res?.data?.createNote?.note?.id?.length) {
          setCurrentNote({
            ...res?.data?.createNote?.note,
            title: t('Common:KnowledgeBase.UntitledNote'),
          });
          //   setViewMode(ViewMode.Notes);
          showSnackbar({ message: t('Common:KnowledgeBase.NoteCreated') });
        } else {
          showErrorSnackbar({ message: t('Common:Error.SomethingWentWrong') });
        }
      })
      .catch(() => {
        showErrorSnackbar({ message: t('Common:Error.SomethingWentWrong') });
      });
  }, [createNote, setCurrentNote, showErrorSnackbar, showSnackbar, viewMode, t]);

  if (isMobile && currentTab === MobileTabs.Chat) {
    return null;
  }

  if (!isMobile && isCollapsed) {
    return (
      <Box className="relative">
        <Stack
          className="fixed top-[var(--ly-header-height,0px)] z-[50] translate-x-[-5.2rem]"
          direction="col"
          gapY={8}>
          <Tooltip placement="left" title="Show Sidebar">
            <IconButton Icon={SidebarRightOutline} size="xl" variant="shadow" onClick={onToggle} />
          </Tooltip>
          <CreateNewHoverPopover showNotesOnly={true} onNewChat={() => {}} onNewNote={onNewNote} />
        </Stack>
      </Box>
    );
  }
  const showNote = Boolean(currentNote && currentNote?.title?.length);

  return (
    <Box className={cn(height100vhMinusHeader, flexN(2), 'xs:max-w-full')}>
      <Stack
        className={cn(height100vhMinusHeader, 'px-16 pt-10 w-full overflow-y-scroll')}
        direction="col"
        gapY={16}>
        <KnowledgeBaseHeader
          currentNotesTab={currentNotesTab}
          hideCollapseButton={showNote || isMobile}
          hideNoteButtons={hideNoteButtons}
          note={currentNote}
          setCurrentNote={setCurrentNote}
          setHideNoteButtons={setHideNoteButtons}
          showBackButton={showNote}
          showNotesOnly={true}
          viewMode={viewMode}
          onNew={onNewNote}
          onToggleCollapsed={onToggle}
        />

        {showNote && currentNote ? (
          <NotesView key={currentNote.id} note={currentNote} setCurrentNote={setCurrentNote} />
        ) : (
          <Notes
            currentNote={currentNote}
            currentNotesTab={currentNotesTab}
            setCurrentNote={setCurrentNote}
            setCurrentNotesTab={setCurrentNotesTab}
            switchView={setViewMode}
            viewMode={viewMode}
            onNewNote={onNewNote}
          />
        )}
      </Stack>
    </Box>
  );
}

export default NotesSection;
